import { useTranslation } from 'react-i18next';
import { Box, Flex, HStack, Text, Badge } from '@chakra-ui/react';
import { HiMapPin, HiClock } from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import type { ReunificationEventSummary } from '@/services/reunification';

interface EventCardProps {
  event: ReunificationEventSummary;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const statusColors: Record<string, { bg: string; color: string }> = {
  active: { bg: 'red.500', color: 'white' },
  pending: { bg: 'orange.500', color: 'white' },
  paused: { bg: 'yellow.500', color: 'black' },
  completed: { bg: 'green.500', color: 'white' },
  cancelled: { bg: 'neutral.500', color: 'white' },
};

export function EventCard({ event }: EventCardProps) {
  const { t } = useTranslation();
  const colors = statusColors[event.status] ?? statusColors.pending;
  const stats = event.stats;

  return (
    <Link to={`/events/${event.public_id}`} style={{ textDecoration: 'none' }}>
      <Box
        bg="surface.primary"
        border="1px solid"
        borderColor={event.status === 'active' ? 'red.300' : 'border.secondary'}
        borderRadius="lg"
        p={4}
        cursor="pointer"
        transition="all 200ms"
        _hover={{ borderColor: 'border.action', shadow: 'md' }}
      >
        <Flex justify="space-between" align="start" mb={3}>
          <Box flex={1}>
            <Text fontSize="md" fontWeight="600" color="text.primary">
              {event.school_name} — {event.incident_type_display}
            </Text>
            <HStack gap={1} mt={1}>
              <HiMapPin size={14} />
              <Text fontSize="sm" color="text.secondary">
                {event.primary_site_name ?? event.school_name}
              </Text>
            </HStack>
          </Box>
          <Badge bg={colors.bg} color={colors.color} px={2} py={0.5} borderRadius="md" fontSize="xs">
            {event.status_display}
          </Badge>
        </Flex>

        {event.started_at && (
          <HStack gap={4} mb={3}>
            <HStack gap={1}>
              <HiClock size={14} />
              <Text fontSize="xs" color="text.secondary">
                {formatDate(event.started_at)} {formatTime(event.started_at)}
              </Text>
            </HStack>
          </HStack>
        )}

        <Flex gap={4} pt={3} borderTopWidth="1px" borderColor="border.line">
          <Box>
            <Text fontSize="xl" fontWeight="700" color="text.primary">
              {stats.total_students}
            </Text>
            <Text fontSize="xs" color="text.secondary">{t('eventCard.students')}</Text>
          </Box>
          <Box>
            <Text fontSize="xl" fontWeight="700" color="green.500">
              {stats.reunified}
            </Text>
            <Text fontSize="xs" color="text.secondary">{t('eventCard.reunified')}</Text>
          </Box>
          <Box>
            <Text fontSize="xl" fontWeight="700" color="text.primary">
              {stats.released}
            </Text>
            <Text fontSize="xs" color="text.secondary">{t('eventCard.released')}</Text>
          </Box>
          {stats.total_students > 0 && (
            <Box ml="auto" textAlign="right">
              <Text fontSize="xl" fontWeight="700" color="text.action">
                {Math.round((stats.accounted / stats.total_students) * 100)}%
              </Text>
              <Text fontSize="xs" color="text.secondary">{t('eventCard.accounted')}</Text>
            </Box>
          )}
        </Flex>
      </Box>
    </Link>
  );
}
